/**
 * Helper Multi-Région pour Production
 *
 * Détecte la région et construit les URLs API appropriées.
 *
 * Vague 8 (11/10/2026) : un seul schema d'URL, <region>.<domaine> (bouake.conquete.app en PROD,
 * bouake.dev.conquete.app en DEV). La region se lit d'abord dans l'HOTE ; le prefixe de chemin
 * (/bouake/...) n'est qu'un repli pour l'acces local (http://localhost:5090/bouake/...).
 * Les fonctions qui CONSTRUISENT des chemins (buildApiUrl, getBaseUrl, navigateWithRegion,
 * authService.getRedirectionUrl via getRegionFromUrl) ne s'appuient que sur le prefixe de chemin :
 * sur un sous-domaine, elles n'ajoutent rien et le gateway lit la region dans l'hote.
 *
 * Régions supportées:
 * - BOUAKE (Gbêkê)
 * - ALEPE (La Mé)
 * - BOUNDIALI (Bagoué)
 * - ABOBO (Abidjan)
 */

// Liste des régions valides
export const VALID_REGIONS = ['bouake', 'alepe', 'abobo', 'boundiali'] as const;
export type Region = typeof VALID_REGIONS[number];

const estRegion = (valeur: string): valeur is Region => (VALID_REGIONS as readonly string[]).includes(valeur);

/**
 * Region portee par le sous-domaine : bouake.conquete.app -> 'bouake' ; sinon null
 */
export const getRegionFromHost = (): string | null => {
  const labels = window.location.hostname.toLowerCase().split('.');
  if (labels.length < 2) return null;
  return estRegion(labels[0]) ? labels[0] : null;
};

/**
 * Region portee par le prefixe de CHEMIN : /bouake/... -> 'bouake' ; sinon null
 */
export const getRegionFromPath = (): string | null => {
  const regionMatch = window.location.pathname.match(/^\/(bouake|alepe|abobo|boundiali)(?=\/|$)/);
  return regionMatch ? regionMatch[1] : null;
};

/**
 * Région courante : sous-domaine d'abord, chemin en repli (identite : en-tetes, titres).
 */
export const getRegion = (): string | null => getRegionFromHost() || getRegionFromPath();

/**
 * Région du prefixe de CHEMIN uniquement. Sert a construire des chemins : vide sur un sous-domaine
 * (la region vient de l'hote, le chemin ne la porte pas). Pour l'identite, voir getRegion().
 * @returns Le code région (bouake, alepe, etc.) ou null si le chemin ne porte pas de région
 */
export const getRegionFromUrl = (): string | null => getRegionFromPath();

/**
 * Construit l'URL API complète avec le préfixe de région s'il est dans le chemin
 * @param endpoint - L'endpoint API (ex: /api/auth/login)
 * @returns L'URL complète avec région si applicable
 */
export const buildApiUrl = (endpoint: string): string => {
  const region = getRegionFromUrl();
  const base = process.env.REACT_APP_API_URL || '';

  // Prefixe de region dans le chemin (acces local)
  if (region) {
    return `${base}/${region}${endpoint}`;
  }

  // Sous-domaine, ou sans région
  return `${base}${endpoint}`;
};

/**
 * Vérifie si une région est présente dans l'URL (hôte ou chemin)
 * @returns true si une région est détectée
 */
export const isRegionInUrl = (): boolean => {
  return getRegion() !== null;
};

/**
 * Récupère l'URL de base avec le préfixe de région s'il est dans le chemin
 * @returns L'URL origin avec ou sans préfixe région
 */
export const getBaseUrl = (): string => {
  const region = getRegionFromUrl();
  const origin = window.location.origin;
  return region ? `${origin}/${region}` : origin;
};

/**
 * Navigue vers un chemin en préservant le préfixe de région s'il est dans le chemin
 * @param path - Le chemin de destination (ex: /identification)
 */
export const navigateWithRegion = (path: string): void => {
  const region = getRegionFromUrl();
  const newPath = region ? `/${region}${path}` : path;
  window.location.href = newPath;
};

/**
 * Récupère les headers à ajouter aux requêtes API
 * @returns Object avec les headers X-Region et X-Region-DB
 */
export const getRegionHeaders = (): Record<string, string> => {
  const region = getRegion();

  if (!region) {
    return {};
  }

  // Map région vers base de données (toutes en _prod pour production)
  const dbMap: Record<string, string> = {
    bouake: 'conquete_bouake_2025_prod',
    alepe: 'conquete_alepe_2025_prod',
    abobo: 'conquete_abobo_2025_prod',
    boundiali: 'conquete_boundiali_2025_prod'
  };

  return {
    'X-Region': region.toUpperCase(),
    'X-Region-DB': dbMap[region] || ''
  };
};

export default {
  VALID_REGIONS,
  getRegionFromHost,
  getRegionFromPath,
  getRegion,
  getRegionFromUrl,
  buildApiUrl,
  isRegionInUrl,
  getBaseUrl,
  navigateWithRegion,
  getRegionHeaders
};
